/**
 * APOFENIA — PROGRESSO DE SCROLL COMO VARIAVEL CSS
 *
 * `<apf-scroll-progress>` escreve `--apf-progress` em si mesmo: 0 quando o topo
 * do elemento entra pela base da tela, 1 quando a base dele sai pelo topo. Nada
 * mais. Quem decide o que fazer com esse numero e o CSS de cada section.
 *
 * Substitui o GSAP + ScrollTrigger que duas sections chamavam sem que nada os
 * carregasse — as duas libs sairam do layout na migracao para o Dawn 16, mas as
 * chamadas ficaram. O efeito nao era so "sem animacao": em
 * `apf-visual-break.liquid` o titulo e o subtitulo comecavam com `opacity: 0` e
 * era o GSAP que os revelaria, entao a section aparecia com uma imagem e nenhum
 * texto, em duas paginas ativas.
 *
 * INVARIANTES
 *
 * 1. Conteudo nunca some se o JS falhar. Este arquivo nao esconde nada: ele so
 *    escreve um numero. O CSS le com `var(--apf-progress, <padrao>)`, entao sem
 *    JS a peca fica parada numa posicao valida, visivel.
 * 2. `prefers-reduced-motion` desliga o movimento nas duas pontas — aqui, que
 *    para de escrever, e no CSS de cada section, que zera o transform. Uma
 *    ponta so bastaria; as duas evitam depender de qual carregou.
 * 2b. `data-motion="off"` no elemento faz a mesma coisa, e vem do setting
 *    `animations_reveal_on_scroll` do editor de temas. Sao dois pedidos
 *    diferentes com o mesmo destino: um e do visitante, o outro e da loja.
 *    Qualquer um dos dois basta para a peca ficar parada — e visivel, porque
 *    o valor de repouso escrito aqui e uma posicao valida, nao um estado de
 *    erro.
 * 3. Idempotente. O ciclo de vida do custom element cobre load, reconexao e
 *    `shopify:section:load`. `disconnectedCallback` desfaz tudo e libera a
 *    guarda, entao reconectar re-arma sem somar listener.
 * 4. Sem CDN e sem dependencia nova.
 *
 * O listener de scroll so existe enquanto o elemento esta na tela: quem liga e
 * desliga e o IntersectionObserver. Fora da tela nao ha listener nenhum.
 */

if (!customElements.get('apf-scroll-progress')) {
  customElements.define(
    'apf-scroll-progress',
    class ApfScrollProgress extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.frame = null;
        this.onScroll = this.onScroll.bind(this);
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onMotionChange = () => {
          this.desligar();
          this.observer?.disconnect();
          this.observer = null;
          this.semMovimento = this.dataset.motion === 'off' || this.motionPreference.matches;
          if (this.semMovimento || typeof IntersectionObserver === 'undefined') {
            this.style.setProperty('--apf-progress', '0.5');
            return;
          }
          this.observer = new IntersectionObserver((entradas) => {
            for (const entrada of entradas) {
              if (entrada.isIntersecting) this.ligar();
              else this.desligar();
            }
          });
          this.observer.observe(this);
          this.medir();
        };
        this.motionPreference.addEventListener('change', this.onMotionChange);
        this.onMotionChange();
      }

      disconnectedCallback() {
        this.desligar();
        this.motionPreference?.removeEventListener('change', this.onMotionChange);
        this.observer?.disconnect();
        this.observer = null;
        this.bound = false;
      }

      ligar() {
        if (this.escutando) return;
        this.escutando = true;
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.medir();
      }

      desligar() {
        if (!this.escutando) return;
        this.escutando = false;
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      // Scroll dispara muito mais que o navegador pinta. Um frame por pintura.
      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.medir();
        });
      }

      medir() {
        const caixa = this.getBoundingClientRect();
        const altura = window.innerHeight || document.documentElement.clientHeight;
        const percurso = altura + caixa.height;
        if (percurso <= 0) return;

        // 0 = topo do elemento encostando na base da tela.
        // 1 = base do elemento saindo pelo topo.
        const bruto = (altura - caixa.top) / percurso;
        const progresso = Math.min(1, Math.max(0, bruto));
        this.style.setProperty('--apf-progress', progresso.toFixed(4));
      }
    }
  );
}
